import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupportedCountries } from '@/hooks/useSupportedCountries';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, ChevronLeft } from 'lucide-react';

interface CountrySelectorProps {
  className?: string;
}

// Default flag emojis for countries
const flagEmojis: Record<string, string> = {
  US: '🇺🇸',
  CA: '🇨🇦',
  GB: '🇬🇧',
  MX: '🇲🇽',
  AU: '🇦🇺',
  DE: '🇩🇪',
  FR: '🇫🇷',
  ES: '🇪🇸',
  BR: '🇧🇷',
  JP: '🇯🇵',
  RU: '🇷🇺',
  CN: '🇨🇳',
};

const CountrySelector: React.FC<CountrySelectorProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { countries, loading } = useSupportedCountries();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftIndicator, setShowLeftIndicator] = useState(false);
  const [showRightIndicator, setShowRightIndicator] = useState(false);

  const checkScrollable = () => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = scrollRef.current;
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 10;
      const canScrollLeft = scrollLeft > 10;
      setShowRightIndicator(canScrollRight);
      setShowLeftIndicator(canScrollLeft);
    }
  };

  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, [countries]);

  const handleScroll = () => {
    checkScrollable();
  };

  const handleCountryClick = (countryCode: string) => {
    navigate(`/country/${countryCode.toLowerCase()}`);
  };

  if (loading) {
    return (
      <div className={`flex gap-3 overflow-x-auto pb-2 hide-scrollbar ${className}`}>
        {[1, 2].map(i => (
          <Skeleton key={i} className="h-12 w-32 rounded-lg flex-shrink-0" />
        ))}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar"
      >
        {countries.map(country => (
          <Button
            key={country.id}
            variant="outline"
            className="flex items-center gap-2 h-12 px-4 flex-shrink-0 bg-card hover:bg-accent border-border transition-all"
            onClick={() => handleCountryClick(country.code)}
          >
            {country.flagImageUrl ? (
              <img 
                src={country.flagImageUrl} 
                alt={country.name} 
                className="w-6 h-4 object-cover rounded"
              />
            ) : (
              <span className="text-xl">{flagEmojis[country.code] || '🏳️'}</span>
            )}
            <span className="font-medium text-foreground">{country.name}</span>
          </Button>
        ))}
      </div>
      
      {showLeftIndicator && (
        <button
          onClick={() => scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-primary/90 rounded-full p-1.5 shadow-lg cursor-pointer border-none"
        >
          <ChevronLeft className="h-5 w-5 text-primary-foreground" />
        </button>
      )}
      
      {showRightIndicator && (
        <button
          onClick={() => scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-primary/90 rounded-full p-1.5 shadow-lg cursor-pointer border-none"
        >
          <ChevronRight className="h-5 w-5 text-primary-foreground" />
        </button>
      )}
    </div>
  );
};

export default CountrySelector;
